const normalizeText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const toCamelKey = (label = "") => {
  const cleaned = normalizeText(label).replace(/[^a-zA-Z0-9 ]/g, " ");
  const parts = cleaned.split(" ").filter(Boolean);
  if (!parts.length) return "";
  return parts
    .map((part, idx) => {
      const lower = part.toLowerCase();
      if (idx === 0) return lower;
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join("");
};

const isHeaderText = (header) =>
  typeof header === "string" || typeof header === "number";

export const buildSchemaFromColumns = (columns = []) =>
  (columns || [])
    .map((col) => {
      const header = col?.header ?? col?.name ?? col?.title;
      if (!header || !isHeaderText(header)) return null;
      const key =
        typeof col?.accessor === "string" && col.accessor.length
          ? col.accessor
          : toCamelKey(header);
      if (!key) return null;
      return { header: String(header), key };
    })
    .filter(Boolean);

export const buildSchemaFromFields = (fields = []) => {
  const schema = [];
  (fields || []).forEach((field) => {
    if (field?.type === "dateTimeRow") {
      if (field.leftDateName) {
        schema.push({
          header: `${field.label} Date`,
          key: field.leftDateName,
        });
      }
      if (field.rightTimeName) {
        schema.push({
          header: `${field.label} Time`,
          key: field.rightTimeName,
        });
      }
      return;
    }
    if (!field?.label) return;
    const key = field?.name || field?.id || toCamelKey(field.label);
    if (!key) return;
    schema.push({ header: String(field.label), key });
  });
  return schema;
};

const splitToArray = (value) =>
  String(value)
    .split(/[,;|]/)
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeValue = (key, value) => {
  if (value === null || value === undefined) return value;
  const loweredKey = String(key).toLowerCase();
  if (
    /labels?|tags?|groups?|members?|collaborators?|assignees?/i.test(
      loweredKey
    )
  ) {
    if (Array.isArray(value)) return value;
    return splitToArray(value);
  }
  if (typeof value === "string") return value.trim();
  return value;
};

export const normalizeImportedRow = (row = {}, schema = [], options = {}) => {
  const mapped = {};

  const setDeepValue = (obj, path, value) => {
    const keys = path.split(".").filter(Boolean);
    let current = obj;
    keys.forEach((k, idx) => {
      if (idx === keys.length - 1) {
        current[k] = value;
        return;
      }
      if (!current[k] || typeof current[k] !== "object") current[k] = {};
      current = current[k];
    });
  };

  schema.forEach(({ header, key }) => {
    const value =
      row?.[header] ??
      row?.[key] ??
      row?.[toCamelKey(header)] ??
      row?.[normalizeText(header)];
    if (value !== undefined) {
      const normalized = normalizeValue(key, value);
      if (key.includes(".")) setDeepValue(mapped, key, normalized);
      else mapped[key] = normalized;
    }
  });
  return { ...row, ...mapped, ...(options?.extra || {}) };
};

export const ensureRowId = (row, idKey = "id") => {
  if (row?.[idKey]) return row;
  return { ...row, [idKey]: `${Date.now()}_${Math.random()}` };
};

const sampleValueForKey = (key, index = 1) => {
  const lowered = String(key).toLowerCase();
  if (lowered.includes("date")) return `2026-02-0${(index % 9) + 1}`;
  if (lowered.includes("time")) return "10:00";
  if (lowered.includes("email"))
    return `user${index}@company.com`.toLowerCase();
  if (lowered.includes("phone") || lowered.includes("tel"))
    return `+1 555 010${index}`;
  if (lowered.includes("status"))
    return index % 2 === 0 ? "Active" : "Pending";
  if (lowered.includes("priority"))
    return index % 2 === 0 ? "High" : "Medium";
  if (lowered.includes("amount") || lowered.includes("price"))
    return 1000 * index;
  if (lowered.includes("total") || lowered.includes("cost")) return 500 * index;
  if (lowered.includes("label") || lowered.includes("tag"))
    return "Marketing, Sales";
  if (lowered.includes("group")) return "Group A, Group B";
  if (lowered.includes("owner")) return "Jane Cooper";
  if (lowered.includes("project")) return `Project ${index}`;
  if (lowered.includes("title")) return `Sample Title ${index}`;
  if (lowered.includes("name")) return `Sample Name ${index}`;
  if (lowered.includes("description")) return "Short description";
  return `Sample ${key} ${index}`.trim();
};

export const buildSampleData = (schema = [], count = 3) => {
  if (!schema.length) {
    return [
      { "Column 1": "Sample 1", "Column 2": "Sample 2", "Column 3": "Sample 3" },
    ];
  }
  return Array.from({ length: count }, (_, idx) => {
    const row = {};
    schema.forEach(({ header, key }) => {
      row[header] = sampleValueForKey(key || header, idx + 1);
    });
    return row;
  });
};

export const makeFileName = (title = "import") => {
  const slug = normalizeText(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${slug || "import"}_sample.xlsx`;
};
